import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  SUPPORTED_LOCALES,
  getDirection,
  interpolate,
  translator,
  type Catalog,
  type Locale,
} from "./index.js";

/**
 * GT-GFI-AI-LANGUAGE-NATIVE-POLICY-ROUTER-01 (DL-119) — normalize an arbitrary
 * language code (e.g. `ar`, `ar-SA`, `de_DE`, `EN`) to a SUPPORTED locale, or
 * `null` when unsupported. Used to render a dynamic AI/refusal message in the
 * user's QUESTION language, independent of the active UI locale.
 */
export function normalizeToSupportedLocale(loc: string | null | undefined): Locale | null {
  if (!loc) return null;
  const base = loc.toLowerCase().split(/[-_]/)[0] ?? "";
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as Locale) : null;
}

/**
 * A-GTCC-LAZY1 — the `de` catalog is the layered fallback for every other
 * locale, so a key missing from (or a catalog that fails to fetch for) the
 * active locale still renders real copy instead of a raw key.
 *
 * E-BUNDLE-3 — it is NO LONGER fetched alongside the active locale on load.
 * All four catalogs carry the identical 7471 keys (measured 2026-09-10), so on
 * the initial paint the fallback could only ever repeat what the active catalog
 * already answers — a second ~560 kB chunk on the critical path for nothing. It
 * is now fetched only when it is actually needed: when the ACTIVE catalog fails
 * to load (see the effect below), or when it becomes the active locale.
 */
const FALLBACK_LOCALE: Locale = "de";

/**
 * A-GTCC-LAZY1 — one dynamic import per language. Each catalog becomes its own
 * chunk, so an app that does NOT pass `catalogs` ships only the active language
 * instead of bundling all four synchronously.
 *
 * E-I18N-SPLIT1 — these point at the CUSTOMER half of each catalog, not the
 * full one. `admin.*` and `admin_hub.*` are roughly half of every catalog by
 * key count and by transfer size, and no customer screen reads them, so they
 * now live in a second file per language that only the Admin app requests (see
 * `loadAdminCatalog`). The authored source of truth is still the full
 * `src/catalogs/<locale>.json`; both halves are generated from it by
 * `scripts/split-catalogs.mjs` and guarded by `catalogs.split.test.ts`.
 */
const CATALOG_LOADERS: Record<Locale, () => Promise<Catalog>> = {
  en: async () => (await import("./catalogs/customer/en.json")).default as Catalog,
  de: async () => (await import("./catalogs/customer/de.json")).default as Catalog,
  fr: async () => (await import("./catalogs/customer/fr.json")).default as Catalog,
  ar: async () => (await import("./catalogs/customer/ar.json")).default as Catalog,
};

/**
 * E-I18N-SPLIT1 — the `admin.*` / `admin_hub.*` half (minus the two families
 * held back for PR #1972, see the generator), requested only by an app
 * that passes `extraCatalogLoader` (today: `apps/admin`). Its keys are merged
 * ON TOP of the customer half, so the merged catalog is identical to the full
 * one the provider used before the split.
 */
const ADMIN_CATALOG_LOADERS: Record<Locale, () => Promise<Catalog>> = {
  en: async () => (await import("./catalogs/admin/en.json")).default as Catalog,
  de: async () => (await import("./catalogs/admin/de.json")).default as Catalog,
  fr: async () => (await import("./catalogs/admin/fr.json")).default as Catalog,
  ar: async () => (await import("./catalogs/admin/ar.json")).default as Catalog,
};

/**
 * Module-level caches: a catalog is fetched at most once per page load, and
 * concurrent requests for the same locale share a single in-flight promise.
 * Both survive provider remounts, so a locale that has already been seen is
 * available synchronously on the next mount. The admin half gets its own pair
 * so the two can be fetched and cached independently.
 */
const catalogCache = new Map<Locale, Catalog>();
const inflightCatalogs = new Map<Locale, Promise<Catalog>>();
const adminCatalogCache = new Map<Locale, Catalog>();
const inflightAdminCatalogs = new Map<Locale, Promise<Catalog>>();

function loadCached(
  locale: Locale,
  loaders: Record<Locale, () => Promise<Catalog>>,
  cache: Map<Locale, Catalog>,
  inflight: Map<Locale, Promise<Catalog>>,
): Promise<Catalog> {
  const cached = cache.get(locale);
  if (cached) return Promise.resolve(cached);
  const running = inflight.get(locale);
  if (running) return running;
  const pending = loaders[locale]()
    .then((catalog) => {
      cache.set(locale, catalog);
      inflight.delete(locale);
      return catalog;
    })
    .catch((err: unknown) => {
      inflight.delete(locale);
      throw err;
    });
  inflight.set(locale, pending);
  return pending;
}

export function loadCatalog(locale: Locale): Promise<Catalog> {
  return loadCached(locale, CATALOG_LOADERS, catalogCache, inflightCatalogs);
}

/**
 * E-I18N-SPLIT1 — pass this as the provider's `extraCatalogLoader` to give an
 * app the admin half of every catalog on top of the customer half.
 */
export function loadAdminCatalog(locale: Locale): Promise<Catalog> {
  return loadCached(locale, ADMIN_CATALOG_LOADERS, adminCatalogCache, inflightAdminCatalogs);
}

export interface LocaleContextValue {
  locale: Locale;
  direction: "ltr" | "rtl";
  t: (key: string, vars?: Record<string, string | number>) => string;
  /**
   * GT-GFI-AI-LANGUAGE-NATIVE-POLICY-ROUTER-01 (DL-119) — translate a key in a
   * SPECIFIC language (the user's question language), falling back to the active
   * UI locale's catalog, then the key. Lets a refusal/answer follow the question
   * language while the rest of the UI chrome stays in the UI locale.
   */
  translateIn: (
    targetLocale: string | null | undefined,
    key: string,
    vars?: Record<string, string | number>,
  ) => string;
  setLocale: (next: Locale) => void;
  /**
   * A-GTCC-LAZY1 — the locale the user selected whose catalog is still loading,
   * or `null`. `locale` does NOT move until that catalog has arrived (see
   * `setLocale`), so a language switcher can surface the pending selection
   * without the rest of the UI half-switching.
   */
  pendingLocale: Locale | null;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

const STORAGE_KEY = "gt.locale";

function readStoredLocale(fallback: Locale): Locale {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_LOCALES.includes(v as Locale) ? (v as Locale) : fallback;
}

export interface LocaleProviderProps {
  initialLocale: Locale;
  /**
   * A-GTCC-LAZY1 — OPTIONAL. When supplied, catalogs are used exactly as
   * before: fully synchronous, no fetching, no gating. That is the path every
   * test and `apps/web` uses, and its behaviour is unchanged. When omitted, the
   * provider loads only the active locale via dynamic import.
   */
  catalogs?: Record<Locale, Catalog>;
  /**
   * A-GTCC-LAZY1 — rendered INSTEAD of `children` until the first catalog has
   * arrived (dynamic mode only), which prevents a raw-key first paint. Ignored
   * when `catalogs` is supplied.
   */
  fallback?: ReactNode;
  /**
   * E-I18N-SPLIT1 — OPTIONAL, dynamic mode only. An app that needs keys beyond
   * the customer half passes a loader here (today `loadAdminCatalog`, for the
   * `admin.*` / `admin_hub.*` half). The extra catalog is fetched for the same
   * locale as the base one and merged on top of it, so `t` answers exactly as
   * it did before the catalogs were split. Ignored when `catalogs` is supplied.
   */
  extraCatalogLoader?: (locale: Locale) => Promise<Catalog>;
  children: ReactNode;
}

export function LocaleProvider({
  initialLocale,
  catalogs: staticCatalogs,
  fallback = null,
  extraCatalogLoader,
  children,
}: LocaleProviderProps) {
  const isStatic = staticCatalogs !== undefined;
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale(initialLocale));
  const [pendingLocale, setPendingLocale] = useState<Locale | null>(null);
  const [dynamicCatalogs, setDynamicCatalogs] = useState<Partial<Record<Locale, Catalog>>>(() =>
    Object.fromEntries(catalogCache),
  );

  const [extraCatalogs, setExtraCatalogs] = useState<Partial<Record<Locale, Catalog>>>({});

  // E-I18N-SPLIT1 — the extra half sits ON TOP of the base half, per locale.
  // Without a loader this is the exact object it always was, so neither the
  // static path nor an app that does not opt in sees any change here.
  const catalogs = useMemo<Partial<Record<Locale, Catalog>>>(() => {
    const base = staticCatalogs ?? dynamicCatalogs;
    if (extraCatalogLoader === undefined) return base;
    const merged: Partial<Record<Locale, Catalog>> = { ...base };
    for (const loc of SUPPORTED_LOCALES) {
      const extra = extraCatalogs[loc];
      if (extra) merged[loc] = { ...(base[loc] ?? {}), ...extra };
    }
    return merged;
  }, [staticCatalogs, dynamicCatalogs, extraCatalogs, extraCatalogLoader]);

  const direction = getDirection(locale);
  const t = useMemo(
    () => translator(catalogs[locale] ?? catalogs[FALLBACK_LOCALE] ?? {}),
    [catalogs, locale],
  );

  // A-GTCC-LAZY1 / E-BUNDLE-3 — dynamic mode only: fetch the ACTIVE locale and
  // nothing else. Every non-active catalog (the `de` fallback included) stays
  // off the initial path and arrives only on a language switch via `setLocale`.
  // Static mode returns immediately and never touches the network.
  useEffect(() => {
    if (isStatic) return;
    let cancelled = false;
    void loadCatalog(locale)
      .then((catalog) => {
        if (cancelled) return;
        setDynamicCatalogs((prev) =>
          prev[locale] === catalog ? prev : { ...prev, [locale]: catalog },
        );
      })
      .catch(() => {
        // E-BUNDLE-3 — the active catalog did not arrive. THIS is the case the
        // `de` fallback exists for, so fetch it now (and only now) instead of
        // paying for it on every load: without it the screen renders raw keys.
        // A failure here degrades to the key itself (see `translator`); it
        // never breaks the screen.
        if (cancelled || locale === FALLBACK_LOCALE) return;
        void loadCatalog(FALLBACK_LOCALE)
          .then((catalog) => {
            if (cancelled) return;
            setDynamicCatalogs((prev) =>
              prev[FALLBACK_LOCALE] === catalog ? prev : { ...prev, [FALLBACK_LOCALE]: catalog },
            );
          })
          .catch(() => {
            // Nothing left to fall back to; `translator` renders the key.
          });
      });
    return () => {
      cancelled = true;
    };
  }, [isStatic, locale]);

  // E-I18N-SPLIT1 — fetch the extra half for the active locale alongside the
  // base one. A failure is not fatal: the base half still renders every
  // customer key, and the layered fallback in `t` degrades the rest to the key.
  useEffect(() => {
    if (isStatic || extraCatalogLoader === undefined) return;
    let cancelled = false;
    void extraCatalogLoader(locale)
      .then((catalog) => {
        if (cancelled) return;
        setExtraCatalogs((prev) =>
          prev[locale] === catalog ? prev : { ...prev, [locale]: catalog },
        );
      })
      .catch(() => {
        // Admin keys stay missing; `translator` renders the key itself.
      });
    return () => {
      cancelled = true;
    };
  }, [isStatic, locale, extraCatalogLoader]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("lang", locale);
    document.documentElement.setAttribute("dir", direction);
  }, [locale, direction]);

  // GT-I18N-FULL-MATRIX-CLOSEOUT-PART-2-01 (D-10) — investigation note.
  // A transient raw-key flash was reported on Admin locale switch. This
  // provider is NOT the cause: key-parity is green (so it is not a
  // missing-key fallback), and the locale swap is atomic — `t` recomputes in
  // the SAME render as `setLocaleState(next)` (`t` depends on `[catalogs,
  // locale]`), so no consumer ever renders with a stale `t`. The remaining
  // hypotheses (a consumer caching `t`/`locale` in its own state, a lazy
  // route, or a perceived reflow from the AR `dir=rtl` switch) require a
  // RUNTIME reproduction to confirm. Per the sprint (D-10 is reproduce-first,
  // no speculative fix), the fix is deferred until the flash is reproduced
  // against a running Admin app. Do not patch blindly here.
  //
  // A-GTCC-LAZY1 — the note above also argued from "all four catalogs are
  // bundled synchronously". That premise no longer holds in dynamic mode, so
  // the atomicity is now guaranteed by construction instead: `setLocale` does
  // not commit the new locale until its catalog has resolved, so `locale`,
  // `direction` and `t` still flip within a single render and no intermediate
  // state can render raw keys.
  const setLocale = useCallback(
    (next: Locale) => {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
      if (isStatic || catalogCache.has(next)) {
        setLocaleState(next);
        setPendingLocale(null);
        return;
      }
      setPendingLocale(next);
      // E-I18N-SPLIT1 — commit only once BOTH halves are in, so the swap stays
      // atomic: an admin screen must never render with the new locale's
      // customer half and the old locale's admin half.
      void Promise.all([
        loadCatalog(next),
        extraCatalogLoader ? extraCatalogLoader(next) : Promise.resolve(undefined),
      ])
        .then(([catalog, extra]) => {
          setDynamicCatalogs((prev) => ({ ...prev, [next]: catalog }));
          if (extra) setExtraCatalogs((prev) => ({ ...prev, [next]: extra }));
          setLocaleState(next);
        })
        .catch(() => {
          // Keep the current locale; the switch simply does not happen.
        })
        .finally(() => {
          setPendingLocale(null);
        });
    },
    [isStatic, extraCatalogLoader],
  );

  // GT-GFI-AI-LANGUAGE-NATIVE-POLICY-ROUTER-01 (DL-119) — render a key in a
  // specific language (the question language), layered: target catalog → active
  // UI-locale catalog → `de` → the key itself.
  //
  // A-GTCC-LAZY1 / E-BUNDLE-3 — in dynamic mode the target language is usually
  // NOT loaded (only the active locale is). The first call for such a language
  // therefore renders the layered fallback and kicks off that catalog's load;
  // when it arrives the state update re-renders the consumer in the target
  // language. Because of the layering, no call returns a bare key that the
  // synchronous version would have translated.
  const translateIn = useCallback(
    (
      targetLocale: string | null | undefined,
      key: string,
      vars?: Record<string, string | number>,
    ): string => {
      const target = normalizeToSupportedLocale(targetLocale) ?? locale;
      if (!isStatic && !catalogs[target]) {
        void loadCatalog(target)
          .then((catalog) => {
            setDynamicCatalogs((prev) =>
              prev[target] === catalog ? prev : { ...prev, [target]: catalog },
            );
          })
          .catch(() => {
            // The layered fallback below already covers this.
          });
      }
      const raw =
        catalogs[target]?.[key] ??
        catalogs[locale]?.[key] ??
        catalogs[FALLBACK_LOCALE]?.[key] ??
        key;
      return interpolate(raw, vars);
    },
    [catalogs, locale, isStatic],
  );

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, direction, t, translateIn, setLocale, pendingLocale }),
    [locale, direction, t, translateIn, setLocale, pendingLocale],
  );

  // A-GTCC-LAZY1 — gate the first paint in dynamic mode until a usable catalog
  // exists, so the app never renders raw keys on load. The provider itself is
  // always mounted, so `fallback` may call `useLocale`.
  // E-I18N-SPLIT1 — when an extra half was requested, the gate waits for it as
  // well, otherwise an Admin screen would paint raw `admin.*` keys for one
  // frame while the second file is still in flight.
  const extraReady =
    extraCatalogLoader === undefined ||
    extraCatalogs[locale] !== undefined ||
    extraCatalogs[FALLBACK_LOCALE] !== undefined;
  const ready =
    (isStatic || catalogs[locale] !== undefined || catalogs[FALLBACK_LOCALE] !== undefined) &&
    extraReady;

  return (
    <LocaleContext.Provider value={value}>{ready ? children : fallback}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used inside <LocaleProvider>");
  return ctx;
}
